import React from 'react';
import WikiIcon from '../shared/WikiIcon';
import { ASK_COPY } from './askCopy';

/**
 * Small pieces of the answer card (D-ask §1.3): inline citation chips, the
 * structured answer lines, the grey note box and the READ row.
 */

/** Inline citation chips — the source number only, not clickable. */
export function CiteChips({ cites }) {
    if (!Array.isArray(cites) || cites.length === 0) return null;
    // Keyed by number + position: a number can (rarely) repeat on one line.
    return cites.map((n, index) => (
        <span key={`${n}-${index}`} className="wk-ask-cite" data-cite={n} aria-label={`Source ${n}`}>{n}</span>
    ));
}

/**
 * `lines` in order: `p` lead line, `li` teal-dot bullet, `quote` the
 * quote-exactly block (label + the text in straight double quotes).
 */
export function AskLines({ lines }) {
    if (!Array.isArray(lines) || lines.length === 0) return null;
    return lines.map((line, index) => {
        const key = `${line?.t || 'p'}-${index}`;
        const text = line?.text || '';
        if (line?.t === 'li') {
            return (
                <div key={key} className="wk-ask-li" data-line="li">
                    <span className="wk-ask-dot" aria-hidden="true" />
                    <span>{text}<CiteChips cites={line.cites} /></span>
                </div>
            );
        }
        if (line?.t === 'quote') {
            return (
                <figure key={key} className="wk-ask-quote" data-line="quote">
                    {line.label && <figcaption className="wk-ask-quote-label">{line.label}</figcaption>}
                    <blockquote className="wk-ask-quote-text">
                        &quot;{text}&quot;<CiteChips cites={line.cites} />
                    </blockquote>
                </figure>
            );
        }
        return (
            <p key={key} className="wk-ask-p" data-line="p">{text}<CiteChips cites={line?.cites} /></p>
        );
    });
}

/** The grey info box (e.g. the correction note). */
export function AskNote({ note }) {
    if (!note) return null;
    return (
        <div className="wk-ask-note" data-testid="wiki-ask-note">
            <WikiIcon name="info" size={14} className="wk-ask-note-icon" />
            <span>{note}</span>
        </div>
    );
}

const MOOD_PILL = { ok: 'wk-pill--green', warn: 'wk-pill--orange', bad: 'wk-pill--red' };

/**
 * READ row: Feeling / Stakes / Needs certainty / Trust pills, then
 * "→ stance · stance". Shown to every role.
 */
export function ReadRow({ read, stance }) {
    const stanceList = Array.isArray(stance) ? stance.filter(Boolean) : [];
    if (!read && stanceList.length === 0) return null;
    const pills = [];
    if (read) {
        if (read.mood) {
            pills.push({ key: 'mood', text: `Feeling: ${read.mood}`, tone: MOOD_PILL[read.mood_tone] || '' });
        }
        if (read.stakes !== undefined && read.stakes !== null) {
            pills.push({ key: 'stakes', text: `Stakes: ${read.stakes} of 4`, tone: '' });
        }
        if (read.certainty !== undefined && read.certainty !== null) {
            pills.push({ key: 'certainty', text: `Needs certainty: ${read.certainty} of 4`, tone: '' });
        }
        if (read.trust) {
            pills.push({ key: 'trust', text: `Trust: ${read.trust}`, tone: read.trust === 'Corrected us' ? 'wk-pill--red' : '' });
        }
    }
    return (
        <div className="wk-ask-read" data-testid="wiki-ask-read">
            <span className="wk-ask-read-label">{ASK_COPY.read}</span>
            {pills.map((pill) => (
                <span key={pill.key} className={`wk-pill wk-ask-read-pill ${pill.tone}`.trim()} data-read={pill.key}>
                    {pill.text}
                </span>
            ))}
            {stanceList.length > 0 && (
                <span className="wk-ask-stance">{`→ ${stanceList.join(' · ')}`}</span>
            )}
        </div>
    );
}
